// src/pages/ScreeningWizard.js - Version Finale Corrigée

import React, { useState } from 'react'
import { 
  Upload, 
  CheckCircle, 
  Brain,
  BarChart3,
  Lightbulb,
  ArrowLeft,
  Target,
  Settings,
  Zap,
  AlertTriangle,
  Globe
} from 'lucide-react'
import { Link } from 'react-router-dom'
import { useScreening } from '../context/ScreeningContext'
import ProfessionalHeader from '../components/Layout/ProfessionalHeader'
import AnimatedCard from '../components/Common/AnimatedCard'
import GlowingButton from '../components/Common/GlowingButton'
import ParticlesBackground from '../components/Common/ParticlesBackground'
import ModelSelector from '../components/ModelSelector'

// Import des nouveaux composants
import UploadStep from '../components/Screening/UploadStep'
import DisqualifyingStep from '../components/Screening/DisqualifyingStep'
import SortingStep from '../components/Screening/SortingStep'
import TechnicalStep from '../components/Screening/TechnicalStep'
import LanguagesStep from '../components/Screening/LanguagesStep'

export default function ScreeningWizard() {
  const {
    currentStep,
    totalSteps,
    uploadedFiles,
    criteria,
    analysisResults,
    analyzing,
    progress,
    selectedAIModel,
    setStep,
    nextStep,
    prevStep,
    updateCriteria,
    setAIModel,
    startAnalysis
  } = useScreening()

  const [stepErrors, setStepErrors] = useState({})

  // Configuration des étapes
  const steps = [
    { id: 1, title: 'Upload CV', icon: Upload, description: '' },
    { id: 2, title: 'Profil', icon: Target, description: '' },
    { id: 3, title: 'Disqualifiants', icon: AlertTriangle, description: '' },
    { id: 4, title: 'Tri général', icon: Settings, description: '' },
    { id: 5, title: 'Techniques', icon: Zap, description: '' },
    { id: 6, title: 'Langues', icon: Globe, description: '' },
    { id: 7, title: 'Modèle IA', icon: Brain, description: '' },
    { id: 8, title: 'Analyse', icon: BarChart3, description: '' },
    { id: 9, title: 'Résultats', icon: CheckCircle, description: '' }
  ]

  const progressPercentage = (currentStep / totalSteps) * 100

  // Validation des étapes
  const validateStep = (step) => {
    const errors = {}
    
    switch (step) {
      case 1:
        if (uploadedFiles.length === 0) {
          errors.files = 'Veuillez uploader au moins un CV'
        }
        break
      case 2:
        if (!criteria.selectedProfile && !criteria.profileDescription?.trim()) {
          errors.profile = 'Veuillez sélectionner ou décrire un profil'
        }
        break
      case 4:
        // Validation des ranges
        if (criteria.ageImportant && criteria.ageMin && criteria.ageMax && 
            parseInt(criteria.ageMin) > parseInt(criteria.ageMax)) {
          errors.ageRange = 'L\'âge minimum ne peut pas être supérieur à l\'âge maximum'
        }
        if (criteria.experienceImportant && criteria.expMin && criteria.expMax && 
            parseInt(criteria.expMin) > parseInt(criteria.expMax)) {
          errors.expRange = 'L\'expérience minimum ne peut pas être supérieure à l\'expérience maximum'
        }
        if (criteria.companiesCountImportant && criteria.minCompaniesCount && criteria.maxCompaniesCount && 
            parseInt(criteria.minCompaniesCount) > parseInt(criteria.maxCompaniesCount)) {
          errors.companiesRange = 'Le nombre minimum de sociétés ne peut pas être supérieur au maximum'
        }
        break
    }
    
    setStepErrors(errors)
    return Object.keys(errors).length === 0
  }

  // Gestion de la navigation
  const handleNext = () => {
    console.log('🎯 handleNext appelé - currentStep:', currentStep)
    
    if (validateStep(currentStep)) {
      if (currentStep === 7) {        // Étape 7 pour lancer l'analyse
        console.log('🚀 Déclenchement startAnalysis...')
        startAnalysis()
      } else {
        console.log('➡️ Passage à l\'étape suivante')
        nextStep()
      }
    } else {
      console.log('❌ Validation échouée pour étape:', currentStep)
    }
  }

  const handleCriteriaChange = (field, value) => {
    updateCriteria(field, value)
  }

  // ÉTAPE 2: Sélection du profil (CORRIGÉE)
  const renderProfileStep = () => {
    const tempProfiles = [
        { 
          id: 'dev-fullstack', 
          title: 'Développeur Full Stack', 
          icon: '💻', 
          description: 'Développement front-end et back-end',
          skills: ['React', 'Node.js', 'JavaScript', 'MongoDB', 'Git']
        },
        { 
          id: 'dev-frontend', 
          title: 'Développeur Front-End', 
          icon: '🎨', 
          description: 'Développement interfaces utilisateur',
          skills: ['React', 'Vue.js', 'CSS', 'HTML', 'JavaScript']
        },
        { 
          id: 'dev-backend', 
          title: 'Développeur Back-End', 
          icon: '⚙️', 
          description: 'Développement serveur et API',
          skills: ['Node.js', 'Python', 'API REST', 'PostgreSQL', 'Docker']
        },
        { 
          id: 'data-scientist', 
          title: 'Data Scientist', 
          icon: '📊', 
          description: 'Analyse et science des données',
          skills: ['Python', 'Machine Learning', 'SQL', 'Tableau', 'R']
        },
        { 
          id: 'comptable', 
          title: 'Comptable', 
          icon: '💰', 
          description: 'Gestion comptable et financière',
          skills: ['Sage', 'Excel', 'DCG/DSCG', 'Fiscalité', 'Audit']
        },
        { 
          id: 'marketing', 
          title: 'Marketing Manager', 
          icon: '📈', 
          description: 'Stratégie et gestion marketing',
          skills: ['Google Ads', 'Analytics', 'SEO', 'Social Media', 'CRM']
        },
        { 
          id: 'commercial', 
          title: 'Commercial', 
          icon: '🤝', 
          description: 'Développement commercial et vente',
          skills: ['Prospection', 'Négociation', 'CRM Salesforce', 'B2B', 'Closing']
        },
        { 
          id: 'rh', 
          title: 'Responsable RH', 
          icon: '👥', 
          description: 'Gestion des ressources humaines',
          skills: ['Recrutement', 'SIRH', 'Droit social', 'Formation', 'Paie']
        },
        { 
          id: 'autre', 
          title: 'Autre profil', 
          icon: '📋', 
          description: 'Profil personnalisé',
          skills: []
        }
      ]

    return (
      <div className="space-y-8">
        <div className="text-center">
          <div className="relative inline-block mb-6">
            <div className="absolute inset-0 bg-gradient-to-r from-purple-500 to-blue-500 rounded-3xl blur-lg opacity-50 animate-pulse"></div>
            <div className="relative w-20 h-20 bg-gradient-to-r from-purple-500 to-blue-500 rounded-3xl flex items-center justify-center shadow-2xl">
              <Target className="w-10 h-10 text-white" />
            </div>
          </div>
          <h2 className="text-4xl font-bold text-white mb-4">Quel profil recherchez-vous ?</h2>
          <p className="text-gray-400 text-lg max-w-3xl mx-auto leading-relaxed">
            Sélectionnez le type de profil professionnel pour personnaliser les critères d'évaluation
          </p>
        </div>

        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {tempProfiles.map((profile) => (
              <div
                key={profile.id}
                onClick={() => {
                  handleCriteriaChange('selectedProfile', profile.id)
                  // Nettoyer la description si un profil prédéfini est sélectionné
                  if (profile.id !== 'autre') {
                    handleCriteriaChange('profileDescription', '')
                  }
                }}
                className={`group relative p-5 rounded-xl border-2 cursor-pointer transition-all duration-300 transform hover:scale-105 ${
                  criteria.selectedProfile === profile.id
                    ? 'bg-gradient-to-br from-blue-500/20 to-purple-500/20 border-blue-500 shadow-xl shadow-blue-500/25'
                    : 'bg-white/5 border-gray-600 hover:border-blue-400 hover:bg-blue-500/10'
                }`}
              >
                {/* Indicateur de sélection */}
                {criteria.selectedProfile === profile.id && (
                  <div className="absolute top-3 right-3 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center animate-pulse">
                    <CheckCircle className="w-4 h-4 text-white" />
                  </div>
                )}

                {/* Contenu */}
            <div className="text-center space-y-3">
            <div className="text-3xl mb-3 group-hover:scale-110 transition-transform">
                {profile.icon}
            </div>
            <h4 className="text-lg font-bold text-white group-hover:text-blue-300 transition-colors">
                {profile.title}
            </h4>
            <p className="text-gray-400 text-sm leading-relaxed">
                {profile.description}
            </p>
            
            {/* Aperçu des compétences clés */}
            {profile.skills && profile.skills.length > 0 && (
                <div className="mt-4 pt-3 border-t border-gray-600">
                <div className="text-xs text-gray-500 mb-2">Compétences clés :</div>
                <div className="flex flex-wrap gap-1 justify-center">
                    {profile.skills.slice(0, 3).map((skill, i) => (
                    <span key={i} className="px-2 py-1 bg-gray-700 text-gray-300 rounded text-xs">
                        {skill}
                    </span>
                    ))}
                    {profile.skills.length > 3 && (
                    <span className="px-2 py-1 bg-gray-700 text-gray-400 rounded text-xs">
                        +{profile.skills.length - 3}
                    </span>
                    )}
                </div>
                </div>
            )}
            </div>
              </div>
            ))}
          </div>

          {/* Description personnalisée - CORRIGÉE */}
          {criteria.selectedProfile && (
            <div className="mt-8 p-6 bg-gradient-to-r from-gray-500/10 to-gray-600/10 rounded-xl border border-gray-500">
              <h4 className="text-white font-medium mb-4 flex items-center">
                <span className="text-2xl mr-3">📝</span>
                {criteria.selectedProfile === 'autre' ? 'Décrivez votre profil personnalisé' : 'Précisez votre recherche (optionnel)'}
              </h4>
             
              <textarea
  value={criteria.profileDescription || ''}
  onChange={(e) => {
    console.log('📝 Changement textarea:', e.target.value);
    handleCriteriaChange('profileDescription', e.target.value);
  }}
  onFocus={() => console.log('🎯 Textarea focused')}
  onClick={(e) => {
    e.stopPropagation();
    console.log('🖱️ Textarea clicked');
  }}
  rows={4}
  style={{ 
    zIndex: 999,
    position: 'relative',
    pointerEvents: 'auto'
  }}
  className="w-full p-4 border border-gray-600 rounded-lg bg-gray-800/50 text-white focus:ring-2 focus:ring-blue-500 focus:outline-none transition-all resize-none"
  placeholder={criteria.selectedProfile === 'autre' 
    ? "Ex: Responsable logistique avec expertise en supply chain et gestion d'entrepôt..."
    : "Ex: Développeur Full Stack avec 3-5 ans d'expérience en React et Node.js, maîtrise d'AWS..."
  }
  autoComplete="off"
  disabled={false}
  readOnly={false}
/>
              <div className="mt-2 text-sm text-gray-400">
                💡 {criteria.selectedProfile === 'autre' 
                  ? 'Décrivez précisément le poste et les compétences recherchées.'
                  : 'Ajoutez des détails spécifiques sur les compétences, l\'expérience ou les qualités recherchées.'
                }
              </div>
              {stepErrors.profile && (
                <div className="mt-3 flex items-center p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                  <AlertTriangle className="w-5 h-5 text-red-400 mr-2" />
                  <span className="text-red-300 text-sm">{stepErrors.profile}</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Résumé de sélection */}
        {criteria.selectedProfile && (
          <div className="max-w-2xl mx-auto p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
            <h5 className="text-blue-300 font-medium mb-2 flex items-center">
              <CheckCircle className="w-5 h-5 mr-2" />
              Profil sélectionné : {tempProfiles.find(p => p.id === criteria.selectedProfile)?.title}
            </h5>
            <p className="text-blue-200 text-sm">
              Les étapes suivantes seront adaptées à ce profil professionnel.
            </p>
          </div>
        )}

        <div className="flex justify-center space-x-4 pt-8">
          <GlowingButton variant="secondary" onClick={prevStep}>
            ← Retour
          </GlowingButton>
          <GlowingButton 
            onClick={handleNext}
            disabled={!criteria.selectedProfile}
            className={criteria.selectedProfile ? '' : 'opacity-50 cursor-not-allowed'}
          >
            Continuer →
          </GlowingButton>
        </div>
      </div>
    )
  }

  // Autres étapes (continuent après...)
  const renderModelSelectionStep = () => {
    const selectedProfileData = {
      'dev-fullstack': 'Développeur Full Stack',
      'dev-frontend': 'Développeur Front-End', 
      'dev-backend': 'Développeur Back-End',
      'data-scientist': 'Data Scientist',
      'comptable': 'Comptable',
      'marketing': 'Marketing Manager',
      'commercial': 'Commercial',
      'rh': 'Responsable RH',
      'autre': 'Profil personnalisé'
    }

    return (
      <div className="space-y-8">
        <div className="text-center mb-8">
          <div className="relative inline-block mb-6">
            <div className="absolute inset-0 bg-gradient-to-r from-purple-500 via-pink-500 to-blue-500 rounded-3xl blur-lg opacity-50 animate-pulse"></div>
            <div className="relative w-20 h-20 bg-gradient-to-r from-purple-500 via-pink-500 to-blue-500 rounded-3xl flex items-center justify-center shadow-2xl">
              <Brain className="w-10 h-10 text-white" />
            </div>
          </div>
          <h2 className="text-4xl font-bold text-white mb-4">
            🧠 Sélection du Cerveau IA
          </h2>
          <p className="text-gray-400 text-lg">
            Choisissez l'intelligence artificielle qui analysera vos{' '}
            <span className="text-blue-400 font-semibold">{uploadedFiles.length} CV</span>
          </p>
        </div>
  
        <div className="max-w-3xl mx-auto">
          <ModelSelector
            selectedModel={selectedAIModel}
            onModelChange={setAIModel}
          />
        </div>
  
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto mt-8">
          <div className="p-6 bg-gradient-to-r from-white/5 to-white/10 rounded-xl border border-white/20 backdrop-blur-sm">
            <h4 className="text-white font-bold mb-4 flex items-center">
              <Brain className="w-6 h-6 mr-3 text-purple-400" />
              Analyse Configurée
            </h4>
            <div className="text-gray-300 space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-400">Profil recherché:</span>
                <span className="font-medium text-blue-400">
                  {selectedProfileData[criteria.selectedProfile] || 'Non défini'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">CV à analyser:</span>
                <span className="font-bold text-green-400">{uploadedFiles.length}</span>
             </div>
             <div className="flex justify-between">
               <span className="text-gray-400">Critères configurés:</span>
               <span className="font-bold text-yellow-400">
                 {Object.keys(criteria).filter(key => 
                   (key.startsWith('disqualifying_') && criteria[key]) ||
                   (key.endsWith('Important') && criteria[key]) ||
                   (key === 'selectedProfile' && criteria[key])
                 ).length}
               </span>
             </div>
           </div>
         </div>
 
         <div className="p-6 bg-gradient-to-r from-white/5 to-white/10 rounded-xl border border-white/20 backdrop-blur-sm">
           <h4 className="text-white font-bold mb-4 flex items-center">
             <BarChart3 className="w-6 h-6 mr-3 text-yellow-400" />
             Estimation
           </h4>
           <div className="text-gray-300 space-y-2">
             <div className="flex justify-between">
               <span className="text-gray-400">Temps estimé:</span>
               <span className="font-bold text-purple-400">~{Math.ceil(uploadedFiles.length * 1.5)} min</span>
             </div>
             <div className="flex justify-between">
               <span className="text-gray-400">Modèle sélectionné:</span>
               <span className="font-bold text-blue-400">{window.multiAIAPI?.models[selectedAIModel]?.name || 'Claude Sonnet 4'}</span>
             </div>
             <div className="flex justify-between">
               <span className="text-gray-400">Type d'analyse:</span>
               <span className="font-bold text-green-400">Professionnelle</span>
             </div>
           </div>
         </div>
       </div>
 
       <div className="max-w-2xl mx-auto">
         <div className="p-6 bg-blue-500/10 border border-blue-500/20 rounded-xl backdrop-blur-sm">
           <div className="flex items-start space-x-3">
             <Lightbulb className="w-6 h-6 text-blue-400 mt-0.5 flex-shrink-0" />
             <div>
               <h6 className="text-blue-300 font-medium mb-2">💡 Conseil de Sélection IA</h6>
               <div className="text-blue-200 text-sm">
                 Le modèle sélectionné analysera les CV avec des critères professionnels avancés adaptés au profil{' '}
                 <span className="font-semibold">{selectedProfileData[criteria.selectedProfile] || 'sélectionné'}</span>.
                 L'analyse prendra en compte tous les critères configurés précédemment.
               </div>
             </div>
           </div>
         </div>
       </div>
 
       <div className="flex justify-center space-x-4 pt-8">
         <GlowingButton variant="secondary" onClick={prevStep}>
           ← Retour
         </GlowingButton>
         <GlowingButton 
           onClick={handleNext} 
           className="bg-gradient-to-r from-purple-500 to-blue-500"
         >
           🚀 Lancer l'Analyse IA
         </GlowingButton>
       </div>
     </div>
   )
 }

 const renderAnalysisStep = () => {
   const selectedProfileData = {
     'dev-fullstack': 'Développeur Full Stack',
     'dev-frontend': 'Développeur Front-End', 
     'dev-backend': 'Développeur Back-End',
     'data-scientist': 'Data Scientist',
     'comptable': 'Comptable',
     'marketing': 'Marketing Manager',
     'commercial': 'Commercial',
     'rh': 'Responsable RH',
     'autre': 'Profil personnalisé'
   }

   return (
     <div className="text-center space-y-8">
       <div className="relative inline-block">
         <div className="absolute inset-0 bg-gradient-to-r from-purple-500 to-blue-500 rounded-3xl blur-xl opacity-50 animate-pulse"></div>
         <div className="relative w-24 h-24 bg-gradient-to-r from-purple-500 to-blue-500 rounded-3xl flex items-center justify-center mx-auto">
           <Brain className="w-12 h-12 text-white animate-spin" />
         </div>
       </div>
       
       <h2 className="text-4xl font-bold text-white">Analyse IA Professionnelle en cours...</h2>
       <p className="text-xl text-gray-400">
         <span className="text-blue-400 font-semibold">{window.multiAIAPI?.models[selectedAIModel]?.name || 'Claude Sonnet 4'}</span>{' '}
         analyse vos {uploadedFiles.length} CV 
         {criteria.selectedProfile && (
           <span className="block mt-1">
             pour le profil <span className="text-purple-400 font-semibold">{selectedProfileData[criteria.selectedProfile]}</span>
           </span>
         )}
       </p>
       
       <div className="max-w-md mx-auto space-y-4">
         <div className="flex justify-between text-sm text-gray-400 mb-2">
           <span>{progress.current} / {progress.total} CV analysés</span>
           <span>{Math.round((progress.current / progress.total) * 100)}%</span>
         </div>
         <div className="w-full bg-gray-700 rounded-full h-4 overflow-hidden">
           <div 
             className="bg-gradient-to-r from-purple-500 via-pink-500 to-blue-500 h-4 rounded-full transition-all duration-500 animate-gradient-shift"
             style={{ width: `${(progress.current / progress.total) * 100}%` }}
           />
         </div>
         
         <div className="grid grid-cols-2 gap-4 mt-6">
           <div className="glass-card p-3 text-center">
             <div className="text-lg font-bold text-green-400">{progress.success}</div>
             <div className="text-xs text-gray-400">Succès</div>
           </div>
           <div className="glass-card p-3 text-center">
             <div className="text-lg font-bold text-red-400">{progress.errors}</div>
             <div className="text-xs text-gray-400">Erreurs</div>
           </div>
         </div>
       </div>
       
       <div className="glass-card p-4 max-w-md mx-auto">
         <div className="text-sm text-gray-400 space-y-1">
           <div className="flex items-center justify-center space-x-2">
             <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse"></div>
             <span>Extraction des données CV...</span>
           </div>
           <div className="flex items-center justify-center space-x-2">
             <div className="w-2 h-2 bg-purple-400 rounded-full animate-pulse"></div>
             <span>Analyse avec critères professionnels...</span>
           </div>
           <div className="flex items-center justify-center space-x-2">
             <div className="w-2 h-2 bg-pink-400 rounded-full animate-pulse"></div>
             <span>Évaluation de l'adéquation au poste...</span>
           </div>
         </div>
       </div>
     </div>
   )
 }

 const renderResultsStep = () => {
   const selectedProfileData = {
     'dev-fullstack': 'Développeur Full Stack',
     'dev-frontend': 'Développeur Front-End', 
     'dev-backend': 'Développeur Back-End',
     'data-scientist': 'Data Scientist',
     'comptable': 'Comptable',
     'marketing': 'Marketing Manager',
     'commercial': 'Commercial',
     'rh': 'Responsable RH',
     'autre': 'Profil personnalisé'
   }

   return (
     <div className="text-center space-y-8">
       <div className="relative inline-block">
         <div className="absolute inset-0 bg-gradient-to-r from-green-500 to-blue-500 rounded-3xl blur-lg opacity-50"></div>
         <div className="relative w-24 h-24 bg-gradient-to-r from-green-500 to-blue-500 rounded-3xl flex items-center justify-center shadow-2xl">
           <CheckCircle className="w-12 h-12 text-white" />
         </div>
       </div>
       <h2 className="text-4xl font-bold text-white">Analyse Professionnelle Terminée !</h2>
       <p className="text-xl text-gray-400">
         <span className="text-blue-400 font-semibold">{window.multiAIAPI?.models[selectedAIModel]?.name}</span>{' '}
         a analysé {analysisResults.length} candidats
         {criteria.selectedProfile && (
           <span className="block mt-1">
             pour le profil <span className="text-purple-400 font-semibold">{selectedProfileData[criteria.selectedProfile]}</span>
           </span>
         )}
       </p>
       
       <div className="glass-card p-6 max-w-md mx-auto">
         <h3 className="text-white font-bold mb-4">Résultats de l'analyse</h3>
         <div className="space-y-2 text-sm">
           <div className="flex justify-between">
             <span className="text-gray-400">Profil recherché:</span>
             <span className="text-white font-medium">
               {selectedProfileData[criteria.selectedProfile] || 'Standard'}
             </span>
           </div>
           <div className="flex justify-between">
             <span className="text-gray-400">CV analysés:</span>
             <span className="text-white font-medium">{uploadedFiles.length}</span>
           </div>
           <div className="flex justify-between">
             <span className="text-gray-400">Modèle IA utilisé:</span>
             <span className="text-white font-medium">{window.multiAIAPI?.models[selectedAIModel]?.name}</span>
           </div>
           <div className="flex justify-between">
             <span className="text-gray-400">Candidats classés:</span>
             <span className="text-white font-medium">{analysisResults.length}</span>
           </div>
           <div className="flex justify-between">
             <span className="text-gray-400">Succès:</span>
             <span className="text-green-400 font-medium">{progress.success}</span>
           </div>
           <div className="flex justify-between">
             <span className="text-gray-400">Erreurs:</span>
             <span className="text-red-400 font-medium">{progress.errors}</span>
           </div>
         </div>
       </div>
 
       <div className="flex justify-center space-x-4">
         <GlowingButton onClick={() => setStep(1)} variant="secondary">
           🔄 Nouvelle Analyse
         </GlowingButton>
         <Link to="/results">
           <GlowingButton className="bg-gradient-to-r from-green-500 to-blue-500">
             📊 Voir les Résultats Détaillés
           </GlowingButton>
         </Link>
       </div>
     </div>
   )
 }

 // Rendu principal du contenu selon l'étape
 const renderStepContent = () => {
   const commonProps = {
     criteria,
     onCriteriaChange: handleCriteriaChange,
     onNext: handleNext,
     onPrev: prevStep,
     errors: stepErrors
   }

   switch (currentStep) {
     case 1:
       return <UploadStep {...commonProps} />
     case 2:
       return renderProfileStep()
     case 3:
       return <DisqualifyingStep {...commonProps} />
     case 4:
       return <SortingStep {...commonProps} />
     case 5:
       return <TechnicalStep {...commonProps} />
     case 6:
       return <LanguagesStep {...commonProps} />
     case 7:
       return renderModelSelectionStep()
     case 8:
       return renderAnalysisStep()
     case 9:
       return renderResultsStep()
     default:
       return (
         <div className="text-center space-y-8">
           <h2 className="text-3xl font-bold text-white">Étape en développement</h2>
           <div className="flex justify-center space-x-4">
             <GlowingButton variant="secondary" onClick={prevStep}>
               ← Retour
             </GlowingButton>
             <GlowingButton onClick={handleNext}>
               Continuer →
             </GlowingButton>
           </div>
         </div>
       )
   }
 }

 return (
   <div className="min-h-screen relative">
     <ParticlesBackground />
     <ProfessionalHeader />
     
     <main className="relative z-10 pt-24 pb-12 px-4 md:px-8">
       <div className="max-w-6xl mx-auto">
         
         {/* Header amélioré */}
         <div className="flex items-center mb-12">
           <Link 
             to="/" 
             className="mr-6 p-4 text-gray-400 hover:text-white hover:bg-white/10 rounded-xl transition-all group"
           >
             <ArrowLeft className="w-6 h-6 group-hover:-translate-x-1 transition-transform" />
           </Link>
           <div className="flex-1">
             <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-pink-400 via-purple-400 to-blue-400 bg-clip-text text-transparent">
               Screening IA Professionnel
             </h1>
             <p className="text-gray-400 mt-2 text-lg">
               Étape {currentStep} sur {totalSteps} • {steps[currentStep - 1]?.description}
             </p>
           </div>
         </div>

         {/* Progress Bar moderne */}
         <div className="mb-16">
           <div className="flex items-center justify-between mb-6 overflow-x-auto pb-2">
             {steps.map((step, index) => (
               <div key={step.id} className={`flex items-center ${index < steps.length - 1 ? 'flex-1' : ''} min-w-0`}>
                 <div className={`relative w-16 h-16 rounded-full flex items-center justify-center text-sm font-semibold transition-all duration-500 ${
                   step.id < currentStep
                     ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white scale-110 shadow-lg'
                     : step.id === currentStep
                     ? 'bg-gradient-to-r from-pink-500 to-blue-500 text-white scale-125 shadow-2xl animate-pulse'
                     : 'bg-gray-700 text-gray-400 scale-100'
                 }`}>
                   {step.id < currentStep ? (
                     <CheckCircle className="w-8 h-8" />
                   ) : (
                     <step.icon className="w-8 h-8" />
                   )}
                   {step.id === currentStep && (
                     <div className="absolute inset-0 bg-gradient-to-r from-pink-500 to-blue-500 rounded-full blur-lg opacity-50 animate-pulse"></div>
                   )}
                 </div>
                 
                 {/* Titre de l'étape */}
                 <div className="ml-3 hidden md:block">
                   <div className={`text-sm font-medium transition-colors ${
                     step.id === currentStep ? 'text-white' : 
                     step.id < currentStep ? 'text-green-400' : 'text-gray-400'
                   }`}>
                     {step.title}
                   </div>
                   <div className="text-xs text-gray-500">{step.description}</div>
                 </div>
                 
                 {index < steps.length - 1 && (
                   <div className="flex-1 h-2 mx-4 bg-gray-700 rounded-full overflow-hidden">
                     <div className={`h-full transition-all duration-700 ${
                       step.id < currentStep 
                         ? 'bg-gradient-to-r from-green-500 to-emerald-500 w-full' 
                         : step.id === currentStep
                         ? 'bg-gradient-to-r from-pink-500 to-blue-500 w-1/2'
                         : 'w-0'
                     }`} />
                   </div>
                 )}
               </div>
             ))}
           </div>
           
           <div className="text-center">
             <div className="text-sm text-gray-400 mb-3">
               Progression: <span className="text-white font-semibold">{Math.round(progressPercentage)}%</span>
               {criteria.selectedProfile && (
                 <span className="ml-4 text-blue-400">
                   • Profil: {criteria.selectedProfile}
                 </span>
               )}
             </div>
             <div className="w-full bg-gray-700 rounded-full h-3 overflow-hidden">
               <div
                 className="h-full bg-gradient-to-r from-pink-500 via-purple-500 to-blue-500 transition-all duration-700 animate-gradient-shift"
                 style={{ width: `${progressPercentage}%` }}
               />
             </div>
           </div>
         </div>

         {/* Contenu avec design moderne */}
         <AnimatedCard className="min-h-[600px] backdrop-blur-xl bg-white/5 border border-white/20">
           {renderStepContent()}
         </AnimatedCard>
       </div>
     </main>
   </div>
 )
}